/**
 * Author products by reference - simple helpers + Shopify Storefront API
 */

(function () {
  'use strict';

  var STOREFRONT_API_URL = '/api/2024-10/graphql.json';
  var STOREFRONT_ACCESS_TOKEN = 'ec8b68546ca482f997b7c781423947ee';
  var PAGE_SIZE = 20;

  /**
   * Get products container for an author (by reference / id)
   * @param {string} authorRef - author reference or id
   * @returns {HTMLElement|null}
   */
  function getAuthorProductsContainer(authorRef) {
    if (!authorRef) return null;
    return document.querySelector('[data-author-ref="' + authorRef + '"]') || null;
  }

  /**
   * Show products for given author reference
   * @param {string} authorRef - author reference
   */
  function showAuthorProducts(authorRef) {
    var container = getAuthorProductsContainer(authorRef);
    if (container) container.style.display = '';
  }

  /**
   * Hide products for given author reference
   * @param {string} authorRef - author reference
   */
  function hideAuthorProducts(authorRef) {
    var container = getAuthorProductsContainer(authorRef);
    if (container) container.style.display = 'none';
  }

  /**
   * Normalise author id for comparison (with or without gid prefix).
   * @param {string} id - author metaobject id (e.g. "gid://shopify/Metaobject/123" or "123")
   * @returns {string}
   */
  function normaliseAuthorId(id) {
    if (!id || typeof id !== 'string') return '';
    var s = id.trim();
    if (s.indexOf('gid://') === 0) return s;
    return 'gid://shopify/Metaobject/' + s;
  }

  /**
   * Check if product's author_references (list metafield) includes the given author id.
   * @param {object} node - product node with metafield.author_references.references
   * @param {string} authorId - author metaobject GID
   * @returns {boolean}
   */
  function productHasAuthor(node, authorId) {
    if (!authorId) return true;
    var meta = node.metafield;
    if (!meta || !meta.references || !meta.references.nodes || !meta.references.nodes.length) return false;
    var want = normaliseAuthorId(authorId);
    for (var i = 0; i < meta.references.nodes.length; i++) {
      if (meta.references.nodes[i] && meta.references.nodes[i].id === want) return true;
    }
    return false;
  }

  /**
   * Map product node to theme shape.
   * @param {object} node - product node
   * @returns {{ image: string, link: string, title: string, product?: object }}
   */
  function mapProductNode(node) {
    var img = node.featuredImage && node.featuredImage.url;
    return {
      image: img || '',
      link: '/products/' + (node.handle || ''),
      title: node.title || '',
      product: { title: node.title }
    };
  }

  /**
   * Fetch products with author_references (list metafield) via Storefront API.
   * If authorId is given, only products that reference this author are returned.
   * @param {string} [authorId] - optional author metaobject GID to filter by
   * @param {number} limit - max products to return (default 20)
   * @returns {Promise<Array<{image: string, link: string, title: string, product?: object}>>}
   */
  function fetchProductsWithAuthorReferences(authorId, limit) {
    limit = limit || 20;
    var collected = [];
    var cursor = null;

    var query = `
      query getProductsWithAuthor($first: Int!, $cursor: String) {
        products(first: $first, after: $cursor) {
          edges {
            node {
              id
              title
              handle
              featuredImage {
                url(transform: { maxWidth: 500, maxHeight: 500 })
              }
              metafield(namespace: "custom", key: "author_references") {
                value
                references(first: 50) {
                  nodes {
                    ... on Metaobject {
                      id
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    function fetchPage() {
      var variables = {
        first: Math.min(PAGE_SIZE, 50),
        cursor: cursor || null
      };
      return fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({ query: query, variables: variables })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Storefront API error: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (data.errors) {
            console.warn('AuthorProductsByReference: GraphQL errors', data.errors);
            return { done: true, hasNext: false };
          }
          var products = data.data && data.data.products;
          if (!products || !products.edges) return { done: true, hasNext: false };
          var pageInfo = products.pageInfo || {};
          for (var i = 0; i < products.edges.length; i++) {
            var node = products.edges[i].node;
            if (!node || !node.id || !node.handle) continue;
            if (!authorId || productHasAuthor(node, authorId)) {
              collected.push(mapProductNode(node));
              if (collected.length >= limit) return { done: true, hasNext: pageInfo.hasNextPage, endCursor: pageInfo.endCursor };
            }
          }
          cursor = pageInfo.endCursor;
          return { done: collected.length >= limit, hasNext: pageInfo.hasNextPage, endCursor: pageInfo.endCursor };
        });
    }

    function loop() {
      return fetchPage().then(function (result) {
        if (result.done || !result.hasNext) return collected.slice(0, limit);
        cursor = result.endCursor;
        return loop();
      });
    }

    return loop().catch(function (err) {
      console.error('AuthorProductsByReference: fetchProductsWithAuthorReferences failed', err);
      return [];
    });
  }

  /**
   * Fetch products that include the given author (via Storefront API search).
   * Uses author name for search; for exact author_references match, use
   * fetchProductsWithAuthorReferences(authorId, limit) instead.
   * @param {string} authorName - author name (or handle) to search
   * @param {number} limit - max products to return (default 20)
   * @returns {Promise<Array<{image: string, link: string, title: string, product?: object}>>}
   */
  function fetchAuthorProductsFromApi(authorName, limit) {
    limit = limit || 20;
    if (!authorName || typeof authorName !== 'string' || authorName.trim() === '') {
      return Promise.resolve([]);
    }

    var query = `
      query authorProductsSearch($query: String!, $first: Int!) {
        search(query: $query, first: $first, types: [PRODUCT]) {
          nodes {
            ... on Product {
              id
              title
              handle
              featuredImage {
                url
                altText
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    var variables = {
      query: authorName.trim(),
      first: Math.min(limit, 50)
    };

    return fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Storefront API error: ' + res.status);
        }
        return res.json();
      })
      .then(function (data) {
        if (data.errors) {
          console.warn('AuthorProductsByReference: GraphQL errors', data.errors);
          return [];
        }
        var nodes = (data.data && data.data.search && data.data.search.nodes) || [];
        return nodes
          .filter(function (/** @type {{ id?: string, handle?: string }} */ node) {
            return node && node.id && node.handle;
          })
          .map(function (/** @type {{ featuredImage?: { url?: string }, handle: string, title?: string }} */ node) {
            return {
              image: (node.featuredImage && node.featuredImage.url) || '',
              link: '/products/' + node.handle,
              title: node.title || '',
              product: { title: node.title }
            };
          });
      })
      .catch(function (err) {
        console.error('AuthorProductsByReference: fetchAuthorProductsFromApi failed', err);
        return [];
      });
  }

  /**
   * Get products for author: when authorId is given, fetch products whose
   * author_references (list metafield) contains this author; else use
   * page-injected list or search by name.
   * @param {string} authorIndex - author index used in window.authorProducts
   * @param {string} authorName - author name for API search fallback
   * @param {number} limit - max products (default 20)
   * @param {string} [authorId] - author metaobject ID (GID or numeric); when set, products are fetched by author_references
   * @returns {Promise<Array<{image: string, link: string, title: string, product?: object}>>}
   */
  function getProductsForAuthor(authorIndex, authorName, limit, authorId) {
    limit = limit || 20;
    if (authorId && (typeof authorId === 'string' && authorId.trim() !== '')) {
      return fetchProductsWithAuthorReferences(authorId, limit);
    }
    var fromPage = (window.authorProducts && authorIndex && window.authorProducts[authorIndex]);
    if (fromPage && Array.isArray(fromPage) && fromPage.length > 0) {
      return Promise.resolve(fromPage);
    }
    return fetchAuthorProductsFromApi(authorName, limit);
  }

  // Expose on window for theme use
  window.AuthorProductsByReference = {
    getContainer: getAuthorProductsContainer,
    show: showAuthorProducts,
    hide: hideAuthorProducts,
    fetchAuthorProducts: fetchAuthorProductsFromApi,
    /** Fetch by author_references list metafield (optional authorId filter). */
    fetchProductsWithAuthorReferences: fetchProductsWithAuthorReferences,
    getProductsForAuthor: getProductsForAuthor
  };
})();
